/**
 * API client for the ``ask_user`` reply endpoint.
 *
 * Agents create AwaitingInput rows via the bridge MCP tool (server-side
 * POST). The browser only submits the user's reply here.
 */

import { apiRequest, type ApiError } from "./client";
import { handleApiError } from "./error-middleware";

export interface ReplyAwaitingInputBody {
  answer: string;
  selectedOption?: string;
}

export interface PendingAwaitingInput {
  id: string;
  /** Null for cursor-anchored (frameless) questions. */
  frame: string | null;
  execution: string;
  question: string;
  context: string;
  options: {
    id: string;
    label: string;
    task?: { title: string; description: string };
    save_tasks?: boolean;
  }[];
  status: string;
  answer: string | null;
  selected_option: string | null;
  timeout_at: string;
  answered_at: string | null;
  created_at: string;
  /** Cursor key — matches what CompanionConsumer emits on cursor.move.
   *  Recipe: runtime_session_id (when linked) || execution.id. Null only when no
   *  agent is attached at all. */
  agent_id: string | null;
  /** Lowercased agent_template.name; null if no agent attached. */
  agent_key: string | null;
  /** Proper-cased agent template name (e.g. "Denker"). */
  name: string | null;
  /** Avatar color set in the agent picker (hex). */
  color: string | null;
  request_id?: string | null;
  conversation_id?: string | null;
  task_id?: string | null;
  /** A real backlog task retained when the contextual proposal was skipped. */
  readonly deferred_task_id?: string | null;
  /** Exact task persisted when the user selected a task proposal. */
  readonly accepted_task_id?: string | null;
  /** Proposal tasks saved without approving execution. */
  readonly saved_task_ids?: string[];
  native_subagent_id?: string | null;
  answer_delivery_status?:
    | ""
    | "pending"
    | "delivered"
    | "unavailable"
    | "superseded";
  answer_delivery_error?: string;
  /** "desktop_approval" for the computer-use approval gate's question. */
  kind?: "desktop_approval" | null;
  /** Its target: a global logical point and display. */
  desktop_target?: { x: number; y: number; display_id: number } | null;
}

export async function listPendingAwaitingInputs(
  projectId: string,
  signal?: AbortSignal,
  options?: { includeDeferred?: boolean },
): Promise<PendingAwaitingInput[]> {
  const res = await apiRequest<{
    status: string;
    data: PendingAwaitingInput[];
  }>(
    `/api/v1/projects/${projectId}/canvas/awaiting-input/${options?.includeDeferred ? "?include_deferred=true" : ""}`,
    { silent: true, signal },
  );
  return res.data;
}

export interface CreateAwaitingInputBody {
  question: string;
  context?: string;
  options: Array<{ id: string; label: string }>;
  agent_key: string;
  frame?: string | null;
  execution?: string;
}

export async function postAwaitingInput(
  projectId: string,
  body: CreateAwaitingInputBody,
): Promise<PendingAwaitingInput> {
  const res = await apiRequest<{ status: string; data: PendingAwaitingInput }>(
    `/api/v1/projects/${projectId}/canvas/awaiting-input/`,
    { method: "POST", body },
  );
  return res.data;
}

/**
 * Submit the user's reply. A 409 means the question was already resolved
 * elsewhere (answered by a follow-up, cancelled, or timed out) and its
 * resolved broadcast was missed, so it resolves like a successful reply and
 * callers clear the stale card without an error.
 */
export async function replyAwaitingInput(
  projectId: string,
  awaitingInputId: string,
  body: ReplyAwaitingInputBody,
): Promise<PendingAwaitingInput | undefined> {
  try {
    const response = await apiRequest<{ data?: PendingAwaitingInput }>(
      `/api/v1/projects/${projectId}/canvas/awaiting-input/${awaitingInputId}/reply/`,
      {
        method: "POST",
        silent: true,
        body: {
          answer: body.answer,
          selected_option: body.selectedOption ?? "",
        },
      },
    );
    return response?.data;
  } catch (error) {
    if ((error as Partial<ApiError> | null)?.status === 409) return;
    handleApiError(error);
    throw error;
  }
}
